<template>
  <div
    class="date-picker"
    :class="{
      'date-picker--error': error,
      'date-picker--disabled': disabled,
      'date-picker--focused': isFocused,
      'date-picker--opened': isOpened,
    }"
  >
    <input
      class="date-picker__input"
      type="text"
      :placeholder="placeholder"
      :value="displayValue"
      :disabled="disabled"
      readonly
      @focus="handleFocus"
      @blur="handleBlur"
      @click="open"
    />

    <button class="date-picker__icon" type="button" :disabled="disabled" aria-label="Открыть календарь" @click="toggle">
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path
          d="M6.88889 6.11111V3V6.11111ZM13.1111 6.11111V3V6.11111ZM6.11111 9.22222H13.8889H6.11111ZM4.55556 17H15.4444C15.857 17 16.2527 16.8361 16.5444 16.5444C16.8361 16.2527 17 15.857 17 15.4444V6.11111C17 5.69855 16.8361 5.30289 16.5444 5.01117C16.2527 4.71944 15.857 4.55556 15.4444 4.55556H4.55556C4.143 4.55556 3.74733 4.71944 3.45561 5.01117C3.16389 5.30289 3 5.69855 3 6.11111V15.4444C3 15.857 3.16389 16.2527 3.45561 16.5444C3.74733 16.8361 4.143 17 4.55556 17Z"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>

    <transition name="date-picker-fade">
      <div v-if="isOpened" class="date-picker__dropdown" v-click-outside="close" @click.stop>
        <v-date-picker :value="value" :locale="locale" :first-day-of-week="firstDayOfWeek" :no-title="noTitle" @input="handleInput" />
      </div>
    </transition>
  </div>
</template>

<script>
import { castDateToLocaleFormat } from '@shared/utils/date'

export default {
  name: 'DatePicker',
  emits: ['input', 'change', 'focus', 'blur'],
  props: {
    value: {
      type: [String, Date, null],
      default: null,
    },
    placeholder: {
      type: String,
      default: '',
    },
    locale: {
      type: String,
      default: '',
    },
    firstDayOfWeek: {
      type: Number,
      default: 1,
    },
    error: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    noTitle: {
      type: Boolean,
      default: true,
    },
    displayFormat: {
      type: String,
      default: 'DD.MM.YYYY',
    },
  },
  data() {
    return {
      isOpened: false,
      isFocused: false,
    }
  },
  computed: {
    displayValue() {
      return castDateToLocaleFormat(this.value)
    },
  },
  beforeDestroy() {
    this.close()
  },
  methods: {
    open() {
      if (this.disabled) {
        return
      }

      this.isOpened = true
    },
    close() {
      this.isOpened = false
    },
    toggle() {
      if (this.disabled) {
        return
      }

      this.isOpened = !this.isOpened
    },
    handleFocus(event) {
      this.isFocused = true
      this.$emit('focus', event)
    },
    handleBlur(event) {
      this.isFocused = false
      this.$emit('blur', event)
    },
    handleInput(value) {
      this.$emit('input', value)
      this.$emit('change', value)
      this.close()
    },
  },
}
</script>

<style lang="sass">
@import "~@src/legacy/sass/params"

.date-picker
  position: relative
  width: 100%

  &--disabled
    opacity: var(--field-opacity-disabled, 0.7)
    pointer-events: none

    .date-picker__input
      background-color: var(--field-disabled-bg-color, #ededed)

  &__input
    width: 100%
    display: block
    padding: 12px 52px 12px 24px
    border-radius: 3px
    border: var(--field-border-width, 1px) solid var(--field-border-color, #C8C8C8)
    color: var(--field-text-color, $black)
    font-size: 16px
    line-height: 140%
    cursor: pointer
    transition: border-color .2s, color .2s, background-color .2s

    &::placeholder
      color: var(--field-placeholder-color, $gray-text)

    &:focus
      outline: none
      border-color: var(--field-border-focus-color, $cyan)

    &:disabled
      cursor: default

  &--focused,
  &--opened
    .date-picker__input
      border-color: var(--field-border-focus-color, $cyan)

  &--error
    .date-picker__input
      border: var(--field-error-border-width, 1px) solid var(--field-error-border-color, $error)
      padding: 11px 51px 11px 23px

  &__icon[type=button]
    position: absolute
    right: 16px
    top: 50%
    z-index: 1
    display: flex
    align-items: center
    justify-content: center
    height: 28px
    padding: 0
    border: 0
    background: transparent
    color: var(--field-icon-color, $cyan)
    cursor: pointer
    transform: translateY(-50%)

    &:disabled
      cursor: default

  &__dropdown
    position: absolute
    top: calc(100% + 2px)
    left: 0
    z-index: 20

    .v-picker__body
      border: var(--field-border-width, 1px) solid var(--field-border-color, #C8C8C8)

  .v-date-picker-table .v-btn
    border-radius: 4px

    &.v-btn--active
      color: #fff
      background-color: #00b0bd !important

  .v-date-picker-header
    & .v-btn--icon
      background: url(/dist/img/arrowDate.svg) no-repeat 50%
      transform: scale(.8)

      &:before
        display: none

      &:last-child
        transform: rotate(180deg) scale(.8)

      & .v-btn__content
        background: url(/dist/img/arrowDateGreen.svg) no-repeat 50%
        opacity: 0
        transition: all .2s

      &:hover .v-btn__content
        opacity: 1

      & .v-ripple__container
        display: none

.date-picker-fade-enter-active,
.date-picker-fade-leave-active
  transition: opacity .2s

.date-picker-fade-enter,
.date-picker-fade-leave-to
  opacity: 0
</style>



// WEBPACK FOOTER //
// src/modules/shared/ui/public/forms/fields/DatePicker.vue